/**
 * The landing page: what needs doing now, across every customer.
 *
 * Shown after sign-in and from the product name on the rail. It answers four questions without
 * opening a list: how many tickets are live and how many are urgent, how much of the fleet is up,
 * what is owed and how much of it is late, and how much work has not been billed yet. Below that, a
 * short list of the things somebody should act on, then the live tickets and the late invoices.
 *
 * Every figure comes from an endpoint another page already uses - there is no dashboard API - and
 * the page re-reads them every minute while it is open.
 */
import { insets, type Container } from 'webwin';
import * as auth from '../api/auth.js';
import * as crm from '../api/crm.js';
import { describeError, request } from '../api/http.js';
import {
  TicketPriority,
  type CustomerDto,
  type DeviceDto,
  type InvoiceDto,
  type LockedAccountDto,
  type TicketDto
} from '../contracts.gen.js';
import { analyzeDevices } from '../deviceAnalytics.js';
import { join, money, plural } from '../format.js';
import { isOpen, isWaiting } from '../rules.js';
import type { Card } from '../ui/Card.js';
import { CardList } from '../ui/CardList.js';
import { chip, type ChipTone } from '../ui/Chip.js';
import { EmptyState } from '../ui/EmptyState.js';
import { link } from '../ui/Link.js';
import { stat } from '../ui/Stat.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  value,
  vstack
} from '../ui/layout.js';
import { deviceAnalyticsSection } from './sections/device-analytics.js';
import { invoiceCard } from './sections/invoice-card.js';
import { ticketCard } from './sections/ticket-card.js';
import type { CrmPage, PageContext } from './page.js';

const REFRESH_MS = 60_000;
const LIST_MAX = 8;

interface AttentionItem {
  readonly key: string;
  readonly tone: ChipTone;
  readonly tag: string;
  readonly title: string;
  readonly detail: string;
  readonly path: string;
}

function greeting(): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const name = auth.currentUser()?.displayName?.split(' ')[0];
  return name ? `${part}, ${name}.` : `${part}.`;
}

/** Local midnight on the first of this month, as an instant. */
function monthStartUtc(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
}

/** Most urgent first, then the most recently touched. */
function byUrgency(a: TicketDto, b: TicketDto): number {
  return b.priority - a.priority || b.updatedUtc.localeCompare(a.updatedUtc);
}

export function dashboardPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      const status = new StatusLine();
      const subtitle = muted(greeting());

      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        h1('Dashboard'),
        subtitle,
        stackOf(buttonRow(), button('Refresh', () => void load())),
        status.label
      );

      // ---- headline figures ----
      const openTickets = stat('Open tickets', 160);
      const fleet = stat('Devices online', 160);
      const outstanding = stat('Outstanding', 160);
      const unbilled = stat('Unbilled work', 160);
      const billedTime = stat('Billable this month', 160);
      const figures = card();
      figures.controls.add(
        stackOf(
          buttonRow({ spacing: GAP.lg, lineSpacing: GAP.md, align: 'start' }),
          openTickets.panel,
          fleet.panel,
          outstanding.panel,
          unbilled.panel,
          billedTime.panel
        )
      );

      // ---- needs attention ----
      const attentionEmpty = new EmptyState('Nothing needs attention right now.');
      const attention = new CardList<AttentionItem>({
        key: a => a.key,
        build: a => attentionRow(a),
        spacing: GAP.sm
      });
      const attentionCard = section('Needs attention', null, attention.panel, attentionEmpty.panel);

      // ---- device charts ----
      const analytics = deviceAnalyticsSection(path => ctx.go(path));

      // ---- live tickets ----
      const ticketsEmpty = new EmptyState('No open tickets.');
      const ticketMore = muted('');
      const tickets = new CardList<TicketDto>({
        key: t => t.id,
        version: t => `${t.updatedUtc}|${t.status}|${t.priority}|${customerName(t.customerId)}`,
        build: t => ticketCard(t, { customerName: customerName(t.customerId), onOpen: x => ctx.go(`/tickets/${x.id}`) }),
        spacing: GAP.sm
      });
      const ticketsCard = section(
        'Open tickets',
        ['All tickets →', () => ctx.go('/tickets')],
        tickets.panel,
        ticketsEmpty.panel,
        ticketMore
      );

      // ---- overdue invoices ----
      const invoicesEmpty = new EmptyState('No invoices are overdue.');
      const invoiceMore = muted('');
      const invoices = new CardList<InvoiceDto>({
        key: i => i.id,
        version: i => `${i.status}|${i.total}|${i.dueUtc ?? ''}`,
        build: i => invoiceCard(i, { showCustomer: true, onOpen: x => ctx.go(`/invoices/${x.id}`) }),
        spacing: GAP.sm
      });
      const invoicesCard = section(
        'Overdue invoices',
        ['All invoices →', () => ctx.go('/invoices?overdue=1')],
        invoices.panel,
        invoicesEmpty.panel,
        invoiceMore
      );

      const body = stackOf(vstack({ spacing: GAP.lg, visible: false }), figures, attentionCard, analytics.panel, ticketsCard, invoicesCard);

      const { view, content } = scrollPage();
      content.controls.add(stackOf(vstack({ spacing: GAP.lg }), header, body));
      host.controls.add(view);

      // =================================================================

      let customers = new Map<number, string>();
      function customerName(id: number): string {
        return customers.get(id) ?? `Customer #${id}`;
      }

      function section(
        title: string,
        more: readonly [string, () => void] | null,
        ...children: Parameters<typeof stackOf>[1][]
      ): Card {
        const heading = splitRow({ align: 'center' });
        fillWith(heading, h2(title));
        if (more) heading.controls.add(link(more[0], more[1]));
        const shell = card();
        shell.controls.add(stackOf(vstack({ spacing: GAP.md }), heading, ...children));
        return shell;
      }

      function attentionRow(a: AttentionItem): Card {
        const left = stackOf(vstack({ spacing: GAP.xs }), value(a.title, { font: FONT.bold }));
        if (a.detail) left.controls.add(muted(a.detail));
        const row = splitRow({ align: 'center', spacing: GAP.md });
        row.controls.add(chip(a.tag, a.tone));
        fillWith(row, left);
        const shell = card({ padding: insets(10, 8), cursor: 'pointer' });
        shell.controls.add(row);
        shell.onActivate(() => ctx.go(a.path));
        return shell;
      }

      function needsAttention(
        open: readonly TicketDto[],
        overdue: readonly InvoiceDto[],
        overdueTotal: number,
        pending: readonly DeviceDto[],
        devices: readonly DeviceDto[],
        locked: readonly LockedAccountDto[]
      ): AttentionItem[] {
        const items: AttentionItem[] = [];

        if (locked.length > 0) {
          items.push({
            key: 'lockouts',
            tone: 'danger',
            tag: 'Security',
            title: `${plural(locked.length, 'account')} locked out`,
            detail: join(locked.slice(0, 3).map(l => l.email)) + (locked.length > 3 ? ' …' : ''),
            path: '/security'
          });
        }

        for (const t of open.filter(x => x.priority === TicketPriority.Urgent).sort(byUrgency).slice(0, 5)) {
          items.push({
            key: `ticket:${t.id}`,
            tone: 'danger',
            tag: 'Urgent',
            title: `#${t.id} ${t.title}`,
            detail: join([customerName(t.customerId), t.statusLabel]),
            path: `/tickets/${t.id}`
          });
        }

        if (overdue.length > 0) {
          items.push({
            key: 'overdue',
            tone: 'danger',
            tag: 'Billing',
            title: `${plural(overdue.length, 'invoice')} overdue, ${money(overdueTotal)}`,
            detail: join([...new Set(overdue.map(i => i.customerName))].slice(0, 3)),
            path: '/invoices?overdue=1'
          });
        }

        if (pending.length > 0) {
          items.push({
            key: 'pending',
            tone: 'warn',
            tag: 'Devices',
            title: `${plural(pending.length, 'device')} waiting for approval`,
            detail: join([...new Set(pending.map(d => d.name))].slice(0, 3)),
            path: '/devices'
          });
        }

        for (const d of devices.filter(x => x.stoppedServices > 0).slice(0, 5)) {
          items.push({
            key: `services:${d.id}`,
            tone: 'warn',
            tag: 'Services',
            title: `${d.name}: ${plural(d.stoppedServices, 'service')} stopped`,
            detail: customerName(d.customerId),
            path: `/devices/${d.id}`
          });
        }

        return items;
      }

      async function load(): Promise<void> {
        if (!body.visible) status.busy('Loading…');
        const signal = ctx.dispose.signal();
        try {
          const lockouts = auth.currentUser()?.role?.toLowerCase() === 'admin'
            ? request<LockedAccountDto[]>('/api/security/lockouts', { signal }).catch(() => [])
            : Promise.resolve([] as LockedAccountDto[]);
          const [customerList, allTickets, invoiceList, devices, pending, presence, summary, locked] = await Promise.all([
            crm.getCustomers(signal),
            crm.getTickets(undefined, signal),
            crm.getAllInvoices({ overdue: true }, signal),
            crm.getDevices(undefined, signal),
            crm.getPendingDevices(undefined, signal),
            crm.getAllPresence(signal),
            crm.getReportSummary(monthStartUtc(), undefined, signal),
            lockouts
          ]);
          if (ctx.cancelled()) return;

          customers = new Map(customerList.map((c: CustomerDto) => [c.id, c.name]));

          const open = allTickets.filter(t => isOpen(t.status));
          const urgent = open.filter(t => t.priority >= TicketPriority.High).length;
          const waiting = open.filter(t => isWaiting(t.status)).length;
          openTickets.set(
            String(open.length),
            join([urgent > 0 ? `${urgent} high or urgent` : null, waiting > 0 ? `${waiting} waiting` : null]),
            urgent > 0 ? 'warn' : 'text'
          );

          const managed = new Set(devices.map(d => d.id));
          const online = presence.filter(p => p.isOnline && managed.has(p.deviceId)).length;
          fleet.set(`${online} of ${devices.length}`, `${devices.length - online} offline`);

          const s = invoiceList.summary;
          outstanding.set(
            money(s.outstandingTotal),
            s.overdueCount > 0 ? `${money(s.overdueTotal)} overdue` : plural(s.outstandingCount, 'invoice'),
            s.overdueCount > 0 ? 'danger' : 'text'
          );
          unbilled.set(money(summary.billing.unbilledValue), 'not yet invoiced');
          const minutes = summary.time.billableMinutes;
          billedTime.set(`${(Math.round((minutes / 60) * 10) / 10).toString()} h`, money(summary.time.billableValue));

          analytics.update(analyzeDevices(devices, presence, customers), {
            devices: new Map(devices.map(d => [d.id, d])),
            presence: new Map(presence.map(p => [p.deviceId, p])),
            customerNames: customers
          });

          const overdue = invoiceList.invoices;
          const items = needsAttention(open, overdue, s.overdueTotal, pending, devices, locked);
          attention.set(items);
          attentionEmpty.update(items.length);

          const sortedOpen = [...open].sort(byUrgency);
          tickets.set(sortedOpen.slice(0, LIST_MAX));
          ticketsEmpty.update(sortedOpen.length);
          ticketMore.text = sortedOpen.length > LIST_MAX ? `and ${sortedOpen.length - LIST_MAX} more` : '';
          ticketMore.visible = sortedOpen.length > LIST_MAX;

          invoices.set(overdue.slice(0, LIST_MAX));
          invoicesEmpty.update(overdue.length);
          invoiceMore.text = overdue.length > LIST_MAX ? `and ${overdue.length - LIST_MAX} more` : '';
          invoiceMore.visible = overdue.length > LIST_MAX;

          subtitle.text = greeting();
          status.clear();
          body.visible = true;
          relayout(body);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the dashboard: ' + describeError(error));
        }
      }

      ctx.dispose.interval(() => void load(), REFRESH_MS);
      void load();
    }
  };
}
